import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { GripVertical, Pencil, Trash2, Plus, UploadCloud, Eye, EyeOff } from "lucide-react";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { listProducts, deleteProduct, reorderProducts, updateProduct } from "@/admin/adminApi";
import { CATEGORY_CONFIGS } from "@/admin/categoryConfig";
import { ImportProductsDialog } from "@/admin/ImportProductsDialog";

const fmt = (n) => (n == null ? "—" : `$${n.toLocaleString("en-NZ")}`);

const BadgeInput = ({ product, onSave }) => {
  const [value, setValue] = useState(product.badge || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => setValue(product.badge || ""), [product.badge]);

  const commit = async () => {
    const trimmed = value.trim();
    if (trimmed === (product.badge || "")) return;
    setSaving(true);
    await onSave(product, trimmed || null);
    setSaving(false);
  };

  return (
    <input
      data-testid={`admin-product-badge-${product.slug}`}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
      disabled={saving}
      placeholder="Badge (e.g. Clearance)"
      title="Label overlaid on the main product image — leave blank for none"
      className="w-32 shrink-0 bg-[var(--bg-main)] border border-[var(--border-default)] px-2 py-1.5 text-xs text-white focus:border-[var(--brand-primary)] outline-none placeholder:text-white/30"
    />
  );
};

const SortableRow = ({ product, hasHiddenToggle, onDelete, onToggleStock, onToggleHidden, onSaveBadge }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: product.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`admin-product-row-${product.slug}`}
      className="flex items-center gap-4 border-b border-[var(--border-default)] py-3 bg-[var(--bg-secondary)]"
    >
      <button {...attributes} {...listeners} data-testid={`admin-product-drag-${product.slug}`} className="cursor-grab text-white/40 hover:text-white shrink-0 px-2">
        <GripVertical size={18} />
      </button>
      <img src={product.thumb} alt={product.name} className="w-16 h-12 object-cover shrink-0 border border-[var(--border-default)]" />
      <div className="flex-1 min-w-0">
        <p className="text-white font-medium truncate">{product.name}</p>
      </div>
      <BadgeInput product={product} onSave={onSaveBadge} />
      {hasHiddenToggle && (
        <label
          className="flex items-center gap-2 shrink-0 cursor-pointer"
          data-testid={`admin-product-hidden-toggle-${product.slug}`}
          title="Hide this product from the storefront (listing, detail page and sitemap) without deleting it"
        >
          <span className="text-[10px] font-mono uppercase tracking-widest text-white/60 w-[52px] text-right flex items-center justify-end gap-1">
            {product.hidden ? <><EyeOff size={12} /> Hidden</> : <><Eye size={12} /> Visible</>}
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={!product.hidden}
            onClick={() => onToggleHidden(product)}
            className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${
              product.hidden ? "bg-white/20" : "bg-[var(--brand-primary)]"
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-black transition-transform ${
                product.hidden ? "" : "translate-x-4"
              }`}
            />
          </button>
        </label>
      )}
      <label
        className="flex items-center gap-2 shrink-0 cursor-pointer"
        data-testid={`admin-product-instock-toggle-${product.slug}`}
        title="Toggle whether Buy Now is available for this product"
      >
        <span className="text-[10px] font-mono uppercase tracking-widest text-white/60 w-[72px] text-right">
          {product.inStock !== false ? "In Stock" : "Buy Now Off"}
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={product.inStock !== false}
          onClick={() => onToggleStock(product)}
          className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${
            product.inStock !== false ? "bg-[var(--brand-primary)]" : "bg-white/20"
          }`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-black transition-transform ${
              product.inStock !== false ? "translate-x-4" : ""
            }`}
          />
        </button>
      </label>
      <div className="text-[var(--brand-primary)] font-mono text-sm w-24 shrink-0">
        {product.priceOnRequest ? "Enquire" : fmt(product.price)}
      </div>
      <Link
        to={`/admin/${product._categorySlug}/${product.slug}`}
        data-testid={`admin-product-edit-${product.slug}`}
        className="text-white/70 hover:text-[var(--brand-primary)] p-2"
      >
        <Pencil size={16} />
      </Link>
      <button
        onClick={() => onDelete(product)}
        data-testid={`admin-product-delete-${product.slug}`}
        className="text-white/70 hover:text-red-400 p-2"
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
};

export default function AdminProductListPage() {
  const { categorySlug } = useParams();
  const config = CATEGORY_CONFIGS[categorySlug];
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const reload = () => {
    if (!config) return;
    setLoading(true);
    setLoadError(null);
    listProducts(config.category)
      .then((data) => {
        setProducts(data.map((p) => ({ ...p, _categorySlug: categorySlug })));
        setLoading(false);
      })
      .catch((err) => {
        setLoadError(err?.response?.data?.detail || "Couldn't load this category's products.");
        setLoading(false);
      });
  };

  useEffect(reload, [config, categorySlug]);

  if (!config) return <Navigate to="/admin/wellside-canopies" replace />;

  if (loadError) {
    return (
      <div data-testid="admin-product-list-error">
        <p className="text-red-400 font-mono text-sm mb-3">{loadError}</p>
        <button type="button" onClick={reload} data-testid="admin-product-list-retry" className="btn-secondary">
          Retry
        </button>
      </div>
    );
  }

  const handleDragEnd = async (event, groupItems) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = groupItems.findIndex((p) => p.id === active.id);
    const newIndex = groupItems.findIndex((p) => p.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reorderedGroup = arrayMove(groupItems, oldIndex, newIndex);
    const reorderedGroupIds = new Set(reorderedGroup.map((p) => p.id));
    let cursor = 0;
    const reordered = products.map((p) => (reorderedGroupIds.has(p.id) ? reorderedGroup[cursor++] : p));
    setProducts(reordered);
    await reorderProducts(config.category, reordered.map((p) => p.id));
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Delete "${product.name}"? This cannot be undone.`)) return;
    await deleteProduct(config.category, product.id);
    setProducts((prev) => prev.filter((p) => p.id !== product.id));
  };

  const handleToggleStock = async (product) => {
    const nextInStock = !(product.inStock !== false);
    setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, inStock: nextInStock } : p)));
    await updateProduct(config.category, product.id, { inStock: nextInStock });
  };

  const handleToggleHidden = async (product) => {
    const nextHidden = !product.hidden;
    setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, hidden: nextHidden } : p)));
    try {
      await updateProduct(config.category, product.id, { hidden: nextHidden });
    } catch (err) {
      setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, hidden: product.hidden } : p)));
      window.alert("Couldn't update visibility — please try again.");
    }
  };

  const handleSaveBadge = async (product, badge) => {
    setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, badge } : p)));
    await updateProduct(config.category, product.id, { badge });
  };

  if (loading) return <p className="text-white/60 font-mono text-sm">Loading…</p>;

  const groups = config.typeOptions?.length > 1
    ? config.typeOptions.map((opt) => ({ key: opt.value, label: opt.label, items: products.filter((p) => p.type === opt.value) }))
    : [{ key: "all", label: null, items: products }];

  return (
    <div data-testid="admin-product-list-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl text-white">{config.label}</h1>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setImportOpen(true)}
            data-testid="admin-product-import-button"
            className="btn-secondary"
          >
            <UploadCloud size={16} /> Import Products
          </button>
          <Link to={`/admin/${categorySlug}/new`} data-testid="admin-product-add-button" className="btn-primary">
            <Plus size={16} /> Add Product
          </Link>
        </div>
      </div>
      <ImportProductsDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        category={config.category}
        onImported={() => { setImportOpen(false); reload(); }}
      />
      <p className="text-white/50 text-sm font-mono mb-4">Drag the handle to reorder how products appear on the storefront. Toggle "In Stock" to control whether Buy Now is available.{config.hasHiddenToggle ? ' Toggle "Visible" to pull a product off the storefront entirely (listing, detail page & sitemap) without deleting it.' : ""} Badge sets the label shown over the main image (blank = none).</p>
      {groups.map((group, gi) => (
        <div key={group.key} className={gi > 0 ? "mt-10" : ""} data-testid={`admin-product-group-${group.key}`}>
          {group.label && (
            <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-white/50 pb-2 mb-1">
              {group.label} <span className="text-white/30">({group.items.length})</span>
            </h2>
          )}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDragEnd(e, group.items)}>
            <SortableContext items={group.items.map((p) => p.id)} strategy={verticalListSortingStrategy}>
              <div className="border-t border-[var(--border-default)]">
                {group.items.map((p) => (
                  <SortableRow key={p.id} product={p} hasHiddenToggle={config.hasHiddenToggle} onDelete={handleDelete} onToggleStock={handleToggleStock} onToggleHidden={handleToggleHidden} onSaveBadge={handleSaveBadge} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      ))}
    </div>
  );
}
